export const THEMES = ['dark', 'light'] as const;
export type Theme = (typeof THEMES)[number];

/** `'system'` follows the OS; the two concrete values are an explicit override. */
export type ThemePreference = Theme | 'system';

const STORAGE_KEY = 'hb-theme';

export function isTheme(value: string): value is Theme {
  return (THEMES as readonly string[]).includes(value);
}

export function isThemePreference(value: string): value is ThemePreference {
  return value === 'system' || isTheme(value);
}

/**
 * Resolves a preference to the theme actually applied.
 *
 * Dark is the fallback rather than light: it is the mode the system is designed
 * in, so an environment with no `matchMedia` (a test, an old WebView) gets the
 * intended look instead of a half-specified one.
 */
export function resolveTheme(preference: ThemePreference, prefersLight = matchesPrefersLight()): Theme {
  if (preference !== 'system') return preference;
  return prefersLight ? 'light' : 'dark';
}

function matchesPrefersLight(): boolean {
  return globalThis.matchMedia?.('(prefers-color-scheme: light)').matches ?? false;
}

export function readStoredPreference(storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage): ThemePreference {
  try {
    const stored = storage?.getItem(STORAGE_KEY);
    return stored && isThemePreference(stored) ? stored : 'system';
  } catch {
    // Safari in private mode throws on localStorage access. A theme is not
    // worth breaking the app over.
    return 'system';
  }
}

export function storePreference(preference: ThemePreference, storage: Pick<Storage, 'setItem'> | undefined = globalThis.localStorage): void {
  try {
    storage?.setItem(STORAGE_KEY, preference);
  } catch {
    // See above - a failure to persist is not a failure to apply.
  }
}

export function applyTheme(theme: Theme, root: Pick<HTMLElement, 'setAttribute'> | undefined = globalThis.document?.documentElement): void {
  root?.setAttribute('data-theme', theme);
}

/**
 * The script that must run in `<head>` BEFORE first paint.
 *
 * Applying the theme from application code is too late: the page renders with
 * the default dark theme and then flips, which is the flash every themed site
 * gets wrong. Inline this string in `index.html` rather than loading it as a
 * file, because a request for a file is also too late.
 *
 * Kept as a string here so the logic lives beside the tokens it applies, and
 * a test can assert it stays in sync with `resolveTheme`.
 */
export const THEME_BOOTSTRAP_SCRIPT = `(function(){try{var s=localStorage.getItem('${STORAGE_KEY}');var t=s==='dark'||s==='light'?s:(window.matchMedia&&window.matchMedia('(prefers-color-scheme: light)').matches?'light':'dark');document.documentElement.setAttribute('data-theme',t);}catch(e){document.documentElement.setAttribute('data-theme','dark');}})();`;

/**
 * Applies the stored preference and keeps following the OS while it is
 * `'system'`. Returns a teardown function.
 */
export function initTheme(): () => void {
  const preference = readStoredPreference();
  applyTheme(resolveTheme(preference));

  const query = globalThis.matchMedia?.('(prefers-color-scheme: light)');
  if (!query) return () => undefined;

  const onChange = (): void => {
    // Only follow the OS while the user has not chosen. Re-reading rather than
    // closing over the value means a preference change elsewhere is respected.
    const current = readStoredPreference();
    if (current === 'system') applyTheme(resolveTheme(current));
  };

  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

/** Stores a preference and applies it immediately. */
export function setThemePreference(preference: ThemePreference): Theme {
  storePreference(preference);
  const theme = resolveTheme(preference);
  applyTheme(theme);
  return theme;
}
